import PropTypes from 'prop-types';

// material-ui
import { Box, Tooltip, Typography, ListItem, ListItemText, Stack } from '@mui/material';

// project imports
import { useSelector } from 'store';
import { isIRI } from 'utils/regexr';
import { getIdFromObj, getIriFromObj } from 'utils/processors';
import Chip from 'ui-component/extended/Chip';
import Icon from '@mui/material/Icon';

export const RENDER_ENTITY_FORMAT0 = 0;
export const RENDER_ENTITY_FORMAT1 = 1;
export const RENDER_ENTITY_FORMAT2 = 2;
export const RENDER_ENTITY_FORMAT3 = 3;
export const RENDER_ENTITY_FORMAT4 = 4;
export const RENDER_ENTITY_FORMAT5 = 5;
export const RENDER_ENTITY_FORMAT6 = 6;

export const getMatchValueInEntity = (match = '', entity = {}) => {
    if (!entity || typeof entity !== 'object') return '';

    if (match.includes('.')) {
        const matches = match.replace(/<|>/g, '').split('.');

        let value = entity;
        for (let i = 0; i < matches?.length; i++) {
            value = value[matches[i]];

            if (!value) return '';
        }

        return typeof value !== 'object' ? value : '';
    }

    return entity[match.replace(/<|>/g, '')] || '';
};

// ==============================|| RENDER GENERIC ENTITY ||============================== //

const RenderEntity = ({
    entityName = '',
    value = null,
    icon = '',
    color = null,
    renderPattern = '',
    renderTooltipPattern = '',
    renderFormat = RENDER_ENTITY_FORMAT0
    // ...others
}) => {
    const { list } = useSelector((state) => state[entityName] || {});

    let entity = {};

    if (typeof value === 'object') {
        Object.assign(entity, value);
    }
    if (typeof value === 'number' && list?.length > 0) {
        entity = list?.find((ent) => getIdFromObj(ent) === value);
    }
    if (typeof value === 'string' && list?.length > 0 && isIRI(value)) {
        entity = list?.find((ent) => getIriFromObj(ent) === value);
    }

    if (!entity) return <Box />;

    const text = renderPattern.replace(/<(.*?)>/g, (match) => getMatchValueInEntity(match, entity));
    const tooltipText = renderTooltipPattern.replace(/<(.*?)>/g, (match) => getMatchValueInEntity(match, entity));

    switch (renderFormat) {
        case RENDER_ENTITY_FORMAT0:
            return <p>{text}</p>;
        case RENDER_ENTITY_FORMAT1:
            return (
                <Typography color="inherit" variant="inherit">
                    {text}
                </Typography>
            );
        case RENDER_ENTITY_FORMAT2:
            return (
                <ListItem>
                    <ListItemText primary={text} />
                </ListItem>
            );
        case RENDER_ENTITY_FORMAT3:
            return (
                <Tooltip title={tooltipText}>
                    <Typography color="inherit" variant="inherit">
                        {text}
                    </Typography>
                </Tooltip>
            );
        case RENDER_ENTITY_FORMAT4:
            return (
                <Tooltip title={tooltipText}>
                    <Stack direction="row">
                        {icon && (
                            <Icon sx={{ fontSize: '0.875rem', color: `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})` }}>{icon}</Icon>
                        )}
                        <Typography color="inherit" variant="inherit">
                            {text}
                        </Typography>
                    </Stack>
                </Tooltip>
            );
        case RENDER_ENTITY_FORMAT5:
            return (
                <Chip
                    label={
                        <Tooltip title={tooltipText}>
                            <Typography color="inherit" variant="inherit">
                                {text}
                            </Typography>
                        </Tooltip>
                    }
                    size="small"
                    variant="outlined"
                    icon={
                        icon && (
                            <Icon
                                sx={{
                                    fontSize: '1.2rem !important',
                                    color: color ? `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})` : 'primary'
                                }}
                            >
                                {icon}
                            </Icon>
                        )
                    }
                    chipcolor={color ? `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})` : 'primary'}
                    sx={{ width: '100%', height: 28 }}
                />
            );
        case RENDER_ENTITY_FORMAT6:
            return (
                <Tooltip title={tooltipText}>
                    <Stack direction="row" justifyContent="flex-start" alignItems="flex-start" spacing={1.5}>
                        {entity?.avatar && (
                            <img loading="lazy" width="20" src={`${process.env.REACT_APP_API}/${entity.avatar}`} alt={text} />
                        )}
                        <Typography color="inherit" variant="inherit">
                            {text}
                        </Typography>
                    </Stack>
                </Tooltip>
            );
        default:
            return <Box />;
    }
};

RenderEntity.propTypes = {
    entityName: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.object, PropTypes.number, PropTypes.string]),
    renderPattern: PropTypes.string,
    icon: PropTypes.string,
    color: PropTypes.object,
    renderTooltipPattern: PropTypes.string,
    renderFormat: PropTypes.oneOf([
        RENDER_ENTITY_FORMAT0,
        RENDER_ENTITY_FORMAT1,
        RENDER_ENTITY_FORMAT2,
        RENDER_ENTITY_FORMAT3,
        RENDER_ENTITY_FORMAT4,
        RENDER_ENTITY_FORMAT5,
        RENDER_ENTITY_FORMAT6
    ])
};

export default RenderEntity;
